import {
  type RequisitionLineInput,
  type Unit,
} from '@eombor/contracts';
import { parseQty, qtyError } from '../../stock/model/receipt-line.js';
import { t } from '../../../shared/i18n/index.js';
import { unitText } from '../../../shared/i18n/labels.js';

/**
 * Talabnoma SAVATI — ekrandagi holat, so'rov tanasi emas.
 *
 * `qty` satr sifatida saqlanadi (`ReceiptLineDraft` bilan bir xil sabab: odam
 * "12." yozib turganda maydonni orqasidan tuzatish kiritishni uzadi). Miqdor
 * o'qish va kasr qoidasi ham SHU YERDA takrorlanmaydi — kirim ekranidagi
 * `parseQty`/`qtyError` qayta ishlatiladi, aks holda bitta ilovada ikki xil
 * miqdor qoidasi paydo bo'lardi.
 *
 * `availableQty` — qidiruv paytida KO'RINGAN mavjud qoldiq. U ogohlantirish
 * uchun saqlanadi va so'rovga ketmaydi: haqiqiy band qilish keyinroq,
 * skladchi qabul qilish qadamida bo'ladi.
 *
 * OMBOR satrning O'ZIDA turadi, hujjatda emas. Sababi domenning qoidasi:
 * talabnoma bitta omborga yoziladi, chunki uni O'SHA ombor skladchisi qabul
 * qiladi. Lekin buyurtmachi mahsulotni qaysi omborda ekanini oldindan
 * bilmaydi — u avval kerakli narsalarni topadi, ular esa turli omborda
 * chiqishi mumkin. Shuning uchun ombor tanlov EMAS, natija: savat oxirida
 * ombor bo'yicha bo'linadi va har bo'lak alohida talabnoma bo'ladi.
 */
export interface BasketLine {
  nomenclatureId: string;
  code: string;
  name: string;
  unit: Unit;
  /** Qaysi ombordan olinadi — hujjat shu bo'yicha bo'linadi. */
  warehouseId: string;
  warehouseName: string;
  /** Mavjud qoldiq AYNAN shu omborda — umumiy yig'indi emas. */
  availableQty: number;
  qty: string;
  note: string;
}

/**
 * Satrning kaliti — ombor va nomenklatura BIRGA.
 *
 * Bir mahsulotni ikki ombordan so'rash haqiqiy hol: "Qarshidan 50 kg,
 * Toshkentdan 30 kg". Kalit faqat nomenklatura bo'lsa, ikkinchi ombor
 * birinchisini jimgina almashtirib qo'yardi.
 */
export const basketKey = (line: {
  warehouseId: string;
  nomenclatureId: string;
}): string => `${line.warehouseId}:${line.nomenclatureId}`;

export const basketLineOf = (input: {
  nomenclatureId: string;
  code: string;
  name: string;
  unit: Unit;
  warehouseId: string;
  warehouseName: string;
  availableQty: number;
}): BasketLine => ({ ...input, qty: '1', note: '' });

/** "mavjud: 12 dona" — miqdor maydonining ostidagi eslatma. */
export const availableText = (line: BasketLine): string =>
  t('requisition.availableQty', {
    qty: String(Math.round(line.availableQty * 1000) / 1000),
    unit: unitText(line.unit),
  });

/**
 * So'ralgan miqdor mavjuddan oshdimi.
 *
 * Ogohlantiradi, lekin BLOKLAMAYDI: qoldiq ekranga chizilgandan keyin ham
 * o'zgaradi va oxirgi qarorni sklad qabul qilish qadamida skladchi chiqaradi.
 */
export const exceedsAvailable = (line: BasketLine): boolean => {
  const qty = parseQty(line.qty);
  return qty !== null && qty > line.availableQty;
};

export const basketQtyError = (line: BasketLine): string | null => qtyError(line.unit, line.qty);

/** Satr yuborishga tayyor: miqdor o'qildi va birlik qoidasidan o'tdi. */
export const isBasketLineReady = (line: BasketLine): boolean =>
  parseQty(line.qty) !== null && basketQtyError(line) === null;

/**
 * Savatni so'rov satrlariga o'giradi — faqat TAYYOR satrlar.
 *
 * Yarim to'ldirilgan satr ekranda xato bilan turadi, shuning uchun uni jimgina
 * tashlab ketish kutilmagan yo'qotish bo'lmaydi.
 */
export const toRequisitionLines = (lines: readonly BasketLine[]): RequisitionLineInput[] =>
  lines.flatMap((line) => {
    const qty = parseQty(line.qty);
    if (qty === null || basketQtyError(line) !== null) return [];
    return [
      {
        nomenclatureId: line.nomenclatureId,
        requestedQty: qty,
        ...(line.note.trim() === '' ? {} : { note: line.note.trim() }),
      },
    ];
  });

/** Savatdagi tayyor satrlar soni — "Yuborish" tugmasi shunga qaraydi. */
export const readyLineCount = (lines: readonly BasketLine[]): number =>
  lines.filter(isBasketLineReady).length;

/**
 * Bitta ombordagi satrlar — kelajakdagi BITTA talabnoma.
 *
 * Guruh ekranda ham shu ko'rinishda chiziladi: odam yuborishdan OLDIN
 * nechta hujjat tug'ilishini ko'rishi kerak. Ilgari bu qoida ekrandan
 * yashiringan edi va "nega ikkita talabnoma chiqdi?" degan savol faqat
 * yuborilgandan keyin tug'ilardi.
 */
export interface BasketGroup {
  warehouseId: string;
  warehouseName: string;
  lines: BasketLine[];
}

/**
 * Savatni ombor bo'yicha bo'ladi — savatga qo'shilish TARTIBINI saqlab.
 *
 * Tartib nom bo'yicha emas, chunki odam o'zi qo'shgan tartibni eslaydi va
 * ro'yxat har qo'shishda qayta saralanib ketsa, ko'z yozib qoladi.
 */
export const groupByWarehouse = (lines: readonly BasketLine[]): BasketGroup[] => {
  const groups = new Map<string, BasketGroup>();
  for (const line of lines) {
    const group = groups.get(line.warehouseId);
    if (group === undefined) {
      groups.set(line.warehouseId, {
        warehouseId: line.warehouseId,
        warehouseName: line.warehouseName,
        lines: [line],
      });
      continue;
    }
    group.lines.push(line);
  }
  return [...groups.values()];
};

/**
 * Nechta talabnoma tug'iladi — faqat TAYYOR satri bor omborlar sanaladi.
 *
 * Butun bir guruhda tayyor satr bo'lmasa, u hujjat ham bo'lmaydi:
 * `toRequisitionLines` uni bo'sh qaytaradi va server bo'sh talabnomani
 * to'g'ri rad etardi — lekin odam buni faqat xato oynasida bilardi.
 */
export const readyGroups = (lines: readonly BasketLine[]): BasketGroup[] =>
  groupByWarehouse(lines).filter((group) => group.lines.some(isBasketLineReady));
